import AppAction from './actionTypes';
import {
  CountryCode,
  PageType,
  PaymentSchemes,
  PaymentSchemeType,
  CountryCodeDisplayNames,
  Currency,
  Language,
} from './constants';
import { getDefaultCurrencyForCountry } from './currencyUtils';

export interface AppState {
  accountProducts: Array<string>;
  countryCode: Array<CountryCode>;
  currency: Currency;
  page: PageType;
  successfulExtraction: boolean;
  amount?: number;
  scheme: PaymentSchemeType | null;
  productExperience?: string;
  forceDesktop: boolean;
  isDonation: boolean;
  recipientId: string | null;
  userFullName: string;
  language: Language;
  customizationName: string | null;
  transactionDays: number;
  accountType: 'personal' | 'business' | 'default' | null;
  pfcVersion: string;
}

export const InitialState: AppState = {
  accountProducts: ['auth', 'transactions', 'identity'],
  countryCode: ['GB'],
  currency: 'GBP',
  page: 'landing',
  scheme: null,
  successfulExtraction: false,
  forceDesktop: false,
  isDonation: false,
  recipientId: '',
  userFullName: '',
  language: 'en',
  customizationName: null,
  transactionDays: 90,
  accountType: null,
  pfcVersion: 'v2',
};

export function stateInitializer(state: AppState): AppState {
  const params = new URLSearchParams(window.location.search);

  let countryCode = [params.get('countryCode') as CountryCode];
  if (!countryCode || !CountryCodeDisplayNames[countryCode[0]]) {
    countryCode = state.countryCode;
  }

  let scheme: PaymentSchemeType | null = params.get(
    'scheme',
  ) as PaymentSchemeType;
  if (!PaymentSchemes.includes(scheme)) {
    scheme = null;
  }

  const forceDesktop = params.has('desktop');

  const recipientId = params.get('recipientId');
  const customizationName = params.get('customizationName');

  return {
    ...state,
    countryCode,
    currency: getDefaultCurrencyForCountry(countryCode),
    scheme,
    forceDesktop,
    recipientId,
    customizationName,
    accountType: null,
  };
}

function AppReducer(
  state: AppState = InitialState,
  action: AppAction,
): AppState {
  switch (action.type) {
    case 'SET_INITIAL_STATE':
      return {
        ...state,
        ...action.payload,
      };
    case 'SET_COUNTRY_CODE':
      return {
        ...state,
        countryCode: action.payload.countryCode,
        currency: getDefaultCurrencyForCountry(action.payload.countryCode),
        productExperience:
          // for landingV2, we don't want to reset the productExperience as we select this
          // before selecting country
          state.page === 'landingV2' ? state.productExperience : '',
        amount: 0,
      };
    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload.language,
      };
    case 'SET_PAGE':
      return {
        ...state,
        page: action.payload.page,
      };
    case 'SET_AMOUNT':
      return {
        ...state,
        amount: action.payload.amount,
      };
    case 'SET_SCHEME':
      return {
        ...state,
        scheme: action.payload.scheme,
      };
    case 'SET_IS_DONATION':
      return {
        ...state,
        isDonation: action.payload.isDonation,
      };
    case 'SET_SUCCESSFUL_EXTRACTION':
      return {
        ...state,
        successfulExtraction: action.payload.successfulExtraction,
      };
    case 'SET_PRODUCT_EXPERIENCE':
      return {
        ...state,
        productExperience: action.payload.productExperience,
      };

    case 'SET_CURRENCY':
      return {
        ...state,
        currency: action.payload.currency,
      };
    case 'SET_NAME':
      return {
        ...state,
        userFullName: action.payload.userFullName,
      };
    case 'SET_ACCOUNT_PRODUCTS':
      return {
        ...state,
        accountProducts: action.payload.accountProducts,
      };
    case 'SET_TRANSACTION_DAYS':
      return {
        ...state,
        transactionDays: action.payload.transactionDays,
      };
    case 'SET_ACCOUNT_TYPE':
      let newAccountType = action.payload.accountType;
      if (newAccountType === state.accountType) {
        // If clicking the same account type again, toggle back to null
        newAccountType = null;
      }
      return {
        ...state,
        accountType: newAccountType,
        customizationName:
          newAccountType === 'business'
            ? 'business_link_experience'
            : newAccountType === 'personal'
              ? 'personal_link_experience'
              : null,
      };
    case 'SET_PFC_VERSION':
      return {
        ...state,
        pfcVersion: action.payload.pfcVersion,
      };
    default:
      return state;
  }
}

export default AppReducer;
